import { faFileImport } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useEffect, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { NoteModalComponent } from '~/_shared/components/modal/noteModal.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { getUserSpreadsheetImportBroadcast } from '~/_shared/utils/api/broadcast.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { type ModalProps } from '~/modal/modalType.enum';
import {
  mapChange, mapReset,
} from '~/store/map/map.actionCreators';
import { useMapInfoDataSelector } from '~/store/mapInfo/mapInfo.selectors';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { useUserIdSelector } from '~/store/userData/userData.selectors';

export type ImportingSpreadsheetModalProps = Readonly<ModalProps>;

export const ImportingSpreadsheetModal: FC<ImportingSpreadsheetModalProps> = ({
  isOpen,
  onClose,
}) => {
  const theme = useTheme();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const mapId = useMapIdSelector();
  const mapInfo = useMapInfoDataSelector();
  const userId = useUserIdSelector();

  const spreadsheetIds = useMemo(() => {
    return mapInfo?.spreadsheets.flatMap(spreadsheet => spreadsheet.realSpreadsheets.map(realSpreadsheet => realSpreadsheet.realSpreadSheetId)) ?? [];
  }, [mapInfo?.spreadsheets]);

  useEffect(() => {
    const onSpreadsheetImportProcessDone = () => {
      onClose();

      dispatch(mapReset());
      if (mapId) {
        dispatch(mapChange(mapId));
      }
    };
    getUserSpreadsheetImportBroadcast(
      e => {
        if ((e.userId === userId) && spreadsheetIds.includes(e.spreadsheetId)) {
          onSpreadsheetImportProcessDone();
        }
      },
      () => onSpreadsheetImportProcessDone(),
    );
  }, [dispatch, mapId, onClose, spreadsheetIds, userId]);

  return (
    <NoteModalComponent
      icon={faFileImport}
      isOpen={isOpen}
      onClose={onClose}
      subTitle={t('importingModal.subtitle')}
      text={t('importingModal.text')}
      textColor={theme.textColors.secondary}
      title={t('importingModal.title')}
    />
  );
};
